import React, {Component} from 'react';
import './SettingsPage.css';
import Page from '../../components/page/Page';
import Card from '../../components/card/Card';
import Label from '../../components/label/Label';
import Divider from '../../components/divider/Divider';
import CheckBox from '../../inputs/checkBox/CheckBox';
import mainAPI from '../../api/MainAPI';
import models from '../../model/models';
import toasts from '../../utils/toasts';
import debounce from 'lodash.debounce';
import Input from '../../inputs/input/Input';
import ChooseGroupPopup from '../../popups/chooseGroupPopup/ChooseGroupPopup';
import utils from '../../utils/utils';
import PrimaryButton from '../../components/primaryButton/PrimaryButton';
import DrugTypeCard from '../../cards/drugTypeCard/DrugTypeCard';
import Integrations from "./Integrations/Integrations";
import NotificationCard from "../../cards/notificationCard/NotificationCard";
import knoxLogo from '../../../src/components/icon/KNOXLogo2.svg'
import userGroupSettingsManager from '../../utils/UserGroupSettingsManager';
import BloodCommUuidCard from "../../cards/bloodCommUuidCard/BloodCommUuidCard";

export const SettingRow = (props) => {
	return <div className={`row ${props.className || ''}`}>{props.children}</div>;
};

export const invalidEmails = (emailsString = '') => {
	const emails = (emailsString || '').split(',');
	const invalidEmails = [];
	for (let em of emails) {
		if (utils.isValidEmail(em.trim())) continue;
		invalidEmails.push(em);
	}
	return invalidEmails.join(',');
};

export default class SettingsPage extends Component {
	constructor(props) {
		super(props);
		this.state = {
			user_group: {},
			activeCard: 'permission',
			autoLogOutAfterHours: '',
			integrations: [],
		};
	}

	componentDidMount = async () => {
		this.subscribeId = models.user_group.onChange(this._getData);
		await this.refreshIntegrations();
	};

	componentWillUnmount = () => {
		if (this.subscribeId) {
			models.user_group.unsubscribe(this.subscribeId);
		}
	};

	fetchIntegrations = async () => {
		try {
			const result = [];
			const integrations = await mainAPI.integration.all() || [];
			const {name, enabled: isEnabled} = integrations.find(({name}) => name === 'NarcLock');
			if (name) {
				const [{
					credentials: {
						account = '',
						password = '',
						email = ''
					} = {}
				} = {}
				] = await mainAPI.narc_lock_integration.get() || [];
				console.log(`Retrieved '${name}' integration details.`);
				result.push({name, isEnabled, account, email, password});
			}
			return result;
		} catch (error) {
			console.error('Error fetching NarcLock integrations and setting state.', error)
			return [];
		}
	};

	refreshIntegrations = async (_integrations = []) => {
		// Note, we could just set state instead of making another API call.
		/// but this is a double check that the child component did update correctly.
		const integrations = await this.fetchIntegrations();
		this.setState({integrations});
	}

	_getData = async () => {
		let drug_types = await mainAPI.drug_type.all_with_allowed();
		let user_group = models.user_group.obj();
		if (!user_group) return;
		await userGroupSettingsManager.refresh(user_group.id);
		this.setState({user_group, drug_types});
	};

	_changeContent = (activeCard) => {
		this.setState({activeCard});
	};

	toggleUserManualScan = (value) => {
		this.toggleBoolean('allow_manual_scan_entry', value);
	};
	toggleRequireInventoryImage = (value) => {
		this.toggleBoolean('require_inventory_image', value);
	};
	toggleRequireResealScanning = (value) => {
		this.toggleBoolean('require_reseal_scanning', value);
	};
	toggleImageUpload = (value) => {
		if (!value) {
			this.toggleRequireInventoryImage(false);
		}
		this.toggleBoolean('allow_image_upload', value);
	};
	toggleKnoxIntegration = (value) => {
		this.toggleBoolean('enable_knox_integration', value);
	};
	toggleInventoryCheckCheckbox = (value) => {
		this.toggleBoolean('allow_daily_check_with_checkbox', value);
		if (!value) this.updateSetting('enable_select_all_inventory_check_option', 0);
	};

	_saveLogoutAfterHours = async (value) => {
		await this.setPositiveNumber('auto_log_out_after_hours', value);
	};

	_updateAutoLogOutAfterHours = debounce(this._saveLogoutAfterHours, 2000);

	setPositiveNumber = async (key, value) => {
		if (value < 1) {
			return toasts.show('Value must be 1 or more');
		}
		let user_group = this.state.user_group || {};
		let userGroupWithSetting = {id: user_group.id};
		userGroupWithSetting[key] = value;
		let res = await mainAPI.user_group.update(userGroupWithSetting);
		if (res) {
			toasts.show('Setting Updated');
		} else {
			toasts.show('Setting did not update');
		}
	};

	toggleUserCreateDrug = (value) => {
		this.toggleBoolean('allow_users_to_create_drugs', value);
	};

	toggleUsageRequiresReseal = (value) => {
		this.toggleBoolean('usage_requires_reseal', value);
	};
	toggleUsageRequiresResealSupply = (value) => {
		this.toggleBoolean('usage_requires_reseal_supply', value);
	};
	toggleRequireDualFactor = (value) => {
		this.toggleBoolean('require_dual_factor', value);
	};

	toggleCanModifyIncident = (value) => {
		this.toggleBoolean('can_modify_incident_number', value);
	};

	toggleAllowReverseDistributor = (value) => {
		this.toggleBoolean('allow_reverse_distributor', value);
	};

	toggleIncidentReportMandatory = (value) => {
		this.toggleBoolean('require_incident_number', value);
	};

	toggleSendExpiringReport = (value) => {
		this.toggleBoolean('send_expiring_report', value);
	};
	toggleMissingDrugNotificationEnabled = (value) => {
		this.toggleBoolean('missing_drug_notification_enabled', value);
	};

	toggleInventoryCheckVerifyRequired = (value) => {
		this.toggleBoolean('daily_check_verify_required', value);
	};
	toggleAllowNdcBarcodeImport = (value) => {
		this.toggleBoolean('allow_ndc_barcode_import', value);
	};

	toggleEnableSelectAllInventoryCheckOption = () => {
		const {
			user_group: {
				allow_daily_check_with_checkbox: check_box = false,
				enable_select_all_inventory_check_option: select_all = false
			} = {}
		} = this.state;
		if (!check_box) return toasts.show('Enable "Users can do inventory checks without scanning drug codes" to use this feature');
		this.toggleBoolean('enable_select_all_inventory_check_option', !select_all);
	};

	toggleSelfWitness = (value) => {
		this.toggleBoolean('allow_self_witness', value);
	};

	toggleBoolean = async (columnName, value) => {
		value = value ? 1 : 0;
		await this.updateSetting(columnName, value);
	};

	updateSetting = async (key, value, successMessage) => {
		let user_group = this.state.user_group || {};
		let res = await mainAPI.user_group.update({id: user_group.id, [key]: value});

		if (res) {
			user_group = this.state.user_group || {};
			user_group[key] = res[key];
			this.setState({user_group});
			toasts.show(successMessage || 'Setting Updated');
		} else {
			toasts.show('Setting did not update');
		}
	};

	setMissingDrugNotificationEmails = debounce((value) => {
		value = value.toLowerCase().replace(/ /g, '');
		const invalid = invalidEmails(value);
		this.setState({invalidMissingDrugNotificationEmails: invalid});
		if (invalid.length > 0) {
			toasts.show('Invalid email');
			return;
		}
		this.updateSetting('missing_drug_notification_emails', value);
	}, 3000);


	updateTimezoneOffset = () => {
		const timezone = getTimezone();
		this.updateSetting('notification_timezone', timezone, 'timezone set to ' + timezone);
	};

	_renderPermissionCard = () => {
		let user_group = this.state.user_group || {};
		return (
			<Card className="inner-card user-settings">
				{false &&
					<SettingRow>
						<CheckBox tabIndex="0" value={!!user_group.enable_knox_integration} onChange={this.toggleKnoxIntegration}/>
						<img className={'knox-logo'} src={knoxLogo} alt="KNOX Logo"/>
						<Label gray-dark>Enable Knox MedVault Integration</Label>
					</SettingRow>
				}
				<Label bold black>Code Scanning & Entry</Label>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.allow_manual_scan_entry} onChange={this.toggleUserManualScan}/>
					<Label gray-dark>Users can manually enter a code without scanning the vial</Label>
				</SettingRow>
				<SettingRow>
					<CheckBox tabIndex="0"
										value={!!user_group.allow_ndc_barcode_import}
										onChange={this.toggleAllowNdcBarcodeImport}
					/>
					<Label gray-dark>Allow scanning the manufacture/NDC barcode to import drug details</Label>
				</SettingRow>
				<br/>
				<Label bold black>Accountability & Witnessing</Label>

				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.allow_self_witness} onChange={this.toggleSelfWitness}/>
					<Label gray-dark>Users can witness their own actions</Label>
				</SettingRow>

				<SettingRow>
					<CheckBox tabIndex="0"
										value={!!user_group.daily_check_verify_required}
										onChange={this.toggleInventoryCheckVerifyRequired}
					/>
					<Label gray-dark>Inventory checks require a witness</Label>
				</SettingRow>

				{false && <>
					<SettingRow>
						<CheckBox tabIndex="0" value={!!user_group.require_inventory_image}
											onChange={this.toggleRequireInventoryImage}/>
						<Label gray-dark>Require images for inventory checks</Label>
					</SettingRow></>}

				<br/>
				<Label bold black>Inventory Management</Label>

				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.allow_daily_check_with_checkbox}
										onChange={this.toggleInventoryCheckCheckbox}/>
					<Label gray-dark>Users can do inventory checks without scanning drug codes</Label>
				</SettingRow>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.enable_select_all_inventory_check_option}
										onChange={this.toggleEnableSelectAllInventoryCheckOption}/>
					<Label gray-dark>Enable "Select All" option during inventory checks</Label>
				</SettingRow>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.allow_image_upload}
										onChange={this.toggleImageUpload}/>
					<Label gray-dark>Enable uploading images during inventory checks</Label>
				</SettingRow>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.require_reseal_scanning}
										onChange={this.toggleRequireResealScanning}/>
					<Label gray-dark>Require scanning codes during a box reseal</Label>
				</SettingRow>

				<br/>
				<Label bold black>Permissions & Editing</Label>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.allow_users_to_create_drugs} onChange={this.toggleUserCreateDrug}/>
					<Label gray-dark>Users "non-admins" can add drugs</Label>
				</SettingRow>


				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.can_modify_incident_number}
										onChange={this.toggleCanModifyIncident}/>
					<Label gray-dark>Users can modify incident numbers</Label>
				</SettingRow>

				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.require_incident_number}
										onChange={this.toggleIncidentReportMandatory}/>
					<Label gray-dark>Require incident number on all administrations</Label>
				</SettingRow>

				<br/>
				<Label bold black>Drug Handling & Compliance</Label>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.allow_reverse_distributor}
										onChange={this.toggleAllowReverseDistributor}/>
					<Label gray-dark>Allow reverse distributors when wasting drugs</Label>
				</SettingRow>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.usage_requires_reseal} onChange={this.toggleUsageRequiresReseal}/>
					<Label gray-dark><b>Deployed</b> boxes must be resealed after waste, administration or box transfers</Label>
				</SettingRow>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.usage_requires_reseal_supply}
										onChange={this.toggleUsageRequiresResealSupply}/>
					<Label gray-dark><b>Supply</b> boxes must be resealed after waste, administration or box transfers</Label>
				</SettingRow>
				<br/>
				<Label bold black>Security & Session Management</Label>
				<SettingRow>
					<CheckBox tabIndex="0" value={!!user_group.require_dual_factor} onChange={this.toggleRequireDualFactor}/>
					<Label gray-dark>Require dual factor authentication</Label>
				</SettingRow>

				<SettingRow>
					<Input
						className="icon"
						value={user_group.auto_log_out_after_hours}
						onChange={this._updateAutoLogOutAfterHours}
					/>
					<Label className="auto-log-off" gray-dark>
						User will automatically be logged off after this many hours of inactivity
					</Label>
				</SettingRow>
			</Card>
		);
	};

	_renderNotificationsCard = () => {
		let {
			send_expiring_report,
			missing_drug_notification_enabled,
			missing_drug_notification_emails,
			notification_timezone,
		} = this.state.user_group || {};
		return (<div className={'notification-container'}><NotificationCard
				after={<Label gray-dark>Additional notifications can be be set for each unit on the unit page.</Label>}/>


				<Card className="inner-card">
					<Label bold large black>
						Other Notifications
					</Label>
					<SettingRow>
						<CheckBox tabIndex="0" value={!!missing_drug_notification_enabled}
											onChange={this.toggleMissingDrugNotificationEnabled}/>
						<Label gray-dark>Send notification for box checks with missing drugs</Label>
					</SettingRow>

					{Boolean(missing_drug_notification_enabled) && <SettingRow className="full-width-input">
						<Label gray-dark>Emails wanting notifications for box checks with missing drugs</Label>
						<Label className={'email-example'} gray-dark>(e.g., email1@gmail.com,email2@yahoo.com)</Label>
						<Input value={missing_drug_notification_emails} onChange={this.setMissingDrugNotificationEmails}/>
						{this.state.invalidMissingDrugNotificationEmails && (
							<Label>invalid emails {this.state.invalidMissingDrugNotificationEmails}</Label>
						)}
					</SettingRow>}

					<SettingRow>
						<CheckBox tabIndex="0" value={!!send_expiring_report} onChange={this.toggleSendExpiringReport}/>
						<Label gray-dark>Send monthly notification of expiring drugs</Label>
					</SettingRow>


				</Card>
				<Card className="inner-card">
					<Label bold large black>
						Notification and Report Timezone
					</Label>

					<SettingRow>
						<PrimaryButton onClick={this.updateTimezoneOffset}>Update Timezone</PrimaryButton>
						<Label gray-dark>Update notification and report exports timezone ({notification_timezone})</Label>
					</SettingRow>
				</Card>
			</div>
		);
	};

	_renderActiveCard = () => {
		const {integrations = []} = this.state;
		if (this.state.activeCard === 'permission') {
			return this._renderPermissionCard();
		} else if (this.state.activeCard === 'drug') {
			return (
				<DrugTypeCard
					className="inner-card drugs-list"
					userGroup={this.state.user_group}
					drugTypes={this.state.drug_types}
				/>
			);
		} else if (this.state.activeCard === 'notifications') {
			return this._renderNotificationsCard();
		} else if (this.state.activeCard === 'integrations') {
			return (<Integrations integrations={integrations} onUpdate={this.refreshIntegrations}/>)
		} else if (this.state.activeCard === 'credentials') {
			// return <TempStickCredentialsCard/>;
			return <BloodCommUuidCard/>;
		}
	};
	renderChangeGroup = () => {
		if (!localStorage.user_group_id) return null;
		return (
			<Label
				bold
				black
				onClick={() => {
					window.popupController.show(ChooseGroupPopup, {});
				}}
			>
				Change Agency
			</Label>
		);
	};

	render() {
		const {user_group: {narc_lock_integration_enabled: narcLockEnabled = false} = {}} = this.state
		return (
			<Page className="g-settings-page">
				<Card>
					<Label larger black>
						Account settings
					</Label>
					<Divider/>
					<div className="lower">
						<div className="list">
							<Label
								onClick={() => {
									this._changeContent('permission');
								}}
								bold
								blue={this.state.activeCard === 'permission'}
								black={this.state.activeCard !== 'permission'}
							>
								General Settings
							</Label>
							<Label
								onClick={() => {
									this._changeContent('drug');
								}}
								bold
								blue={this.state.activeCard === 'drug'}
								black={this.state.activeCard !== 'drug'}
							>
								Available Drugs
							</Label>
							<Label
								onClick={() => {
									this._changeContent('notifications');
								}}
								bold
								blue={this.state.activeCard === 'notifications'}
								black={this.state.activeCard !== 'notifications'}
							>
								Notifications
							</Label>
							{!!narcLockEnabled && (
								<Label
									onClick={() => {
										this._changeContent('integrations');
									}}
									bold
									blue={this.state.activeCard === 'integrations'}
									black={this.state.activeCard !== 'integrations'}
								>
									Integrations
								</Label>
							)}
							{userGroupSettingsManager.getSetting('allow_temperature_sensor') && (
								<Label
									onClick={() => {
										this._changeContent('credentials');
									}}
									bold
									blue={this.state.activeCard === 'credentials'}
									black={this.state.activeCard !== 'credentials'}
								>
									Sensors
								</Label>
							)}
							{this.renderChangeGroup()}
							{/*<Label onClick={this._changeContent} bold black>Notifications</Label>*/}
						</div>
						{this._renderActiveCard()}
					</div>
				</Card>
			</Page>
		);
	}
}
const getTimezone = () => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
};


// WEBPACK FOOTER //
// ./src/pages/settingsPage/SettingsPage.jsx